import { Component } from '@theme/component';
import {
  supportsViewTransitions,
  startViewTransition,
  onAnimationEnd,
  prefersReducedMotion,
  debounce,
  preloadImage,
  isLowPowerDevice,
} from '@theme/utilities';
import { scrollIntoView } from '@theme/scrolling';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';
/**
 * A custom element that renders a zoom dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement[]} media - The media elements.
 * @property {HTMLElement} thumbnails - The thumbnails elements.
 *
 * @extends Component<Refs>
 */
export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];

  #highResImagesLoaded = /** @type {Set<string>} */ (new Set());

  #getActiveIndex() {
    const { thumbnails } = this.refs;
    if (!thumbnails || !(thumbnails instanceof HTMLElement)) return 0;

    const buttons = Array.from(thumbnails.querySelectorAll('button'));
    const selectedIndex = buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
    return selectedIndex >= 0 ? selectedIndex : 0;
  }

  #fitDialogToMedia(index) {
    const normalizedIndex = Number(index);
    const dialog = this.refs.dialog;
    const mediaItem = this.refs.media?.[Number.isFinite(normalizedIndex) ? normalizedIndex : 0];
    if (!dialog || !(dialog instanceof HTMLDialogElement) || !mediaItem) return;

    const isProductPageDialog = !!this.closest("main[data-template*='product']");
    if (!isProductPageDialog) return;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;
    const isCompact = viewportWidth < 1100;
    const viewportPadding = isCompact ? 16 : 24;
    const maxDialogWidth = Math.min(viewportWidth - viewportPadding * 2, isCompact ? 860 : 1180);
    const maxDialogHeight = Math.min(viewportHeight - viewportPadding * 2, isCompact ? 760 : 900);
    const minDialogWidth = isCompact ? 320 : 460;
    const minDialogHeight = isCompact ? 400 : 460;
    const frameChrome = isCompact ? 158 : 178; // header + thumbs + paddings
    const horizontalChrome = isCompact ? 20 : 24; // lateral paddings/borders

    const mediaRoot = mediaItem.querySelector('.product-media');
    const imageElement = mediaItem.querySelector('.product-media__image');
    const ratioCandidates = [
      imageElement && imageElement.naturalWidth > 0 ? imageElement.naturalWidth / imageElement.naturalHeight : null,
      mediaRoot && getComputedStyle(mediaRoot).getPropertyValue('--ratio'),
      getComputedStyle(mediaItem).getPropertyValue('--media-preview-ratio'),
    ];
    let ratio = 1.65;
    for (const candidate of ratioCandidates) {
      const parsed = Number.parseFloat((candidate || '').trim());
      if (Number.isFinite(parsed) && parsed > 0) {
        ratio = parsed;
        break;
      }
    }

    let imageViewportHeight = Math.max(240, maxDialogHeight - frameChrome);
    let dialogWidth = imageViewportHeight * ratio + horizontalChrome;
    let dialogHeight = imageViewportHeight + frameChrome;

    if (dialogWidth > maxDialogWidth) {
      dialogWidth = maxDialogWidth;
      const imageViewportWidth = Math.max(220, dialogWidth - horizontalChrome);
      imageViewportHeight = imageViewportWidth / ratio;
      dialogHeight = imageViewportHeight + frameChrome;
    }

    dialogWidth = Math.max(minDialogWidth, Math.min(maxDialogWidth, dialogWidth));
    dialogHeight = Math.max(minDialogHeight, Math.min(maxDialogHeight, dialogHeight));

    dialog.style.setProperty('width', `${Math.round(dialogWidth)}px`, 'important');
    dialog.style.setProperty('max-width', `${Math.round(dialogWidth)}px`, 'important');
    dialog.style.setProperty('height', `${Math.round(dialogHeight)}px`, 'important');
    dialog.style.setProperty('max-height', `${Math.round(dialogHeight)}px`, 'important');
  }

  #updateCounter(index) {
    const counter = this.refs.dialog?.querySelector('.dialog-gallery-counter');
    if (!counter || !(counter instanceof HTMLElement)) return;
    const total = this.refs.media?.length ?? 0;
    const normalizedIndex = Number(index);
    const safeIndex =
      Number.isFinite(normalizedIndex) && normalizedIndex >= 0 ? Math.min(normalizedIndex, Math.max(total - 1, 0)) : 0;
    counter.textContent = `${safeIndex + 1} / ${total}`;
  }

  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog?.addEventListener('scroll', this.handleScroll);
    window.addEventListener('resize', this.handleResize);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.refs.dialog?.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
  }

  handleResize = () => {
    if (!this.refs.dialog?.open) return;
    this.#fitDialogToMedia(this.#getActiveIndex());
  };

  /**
   * Opens the zoom dialog.
   *
   * @param {number} index - The index of the media to zoom.
   * @param {PointerEvent} event - The pointer event.
   */
  async open(index, event) {
    event?.preventDefault();
    const normalizedIndex = Number(index);
    const mediaIndex = Number.isFinite(normalizedIndex) && normalizedIndex >= 0 ? normalizedIndex : 0;

    const isProductPageDialog = !!this.closest("main[data-template*='product']");
    const isMobileViewport = window.matchMedia('(max-width: 749px)').matches;
    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (this.closest('media-gallery'));

    // On mobile PDP we disable modal zoom and keep the interaction in the main gallery only.
    if (isProductPageDialog && isMobileViewport) {
      const slideshowCount = mediaGallery?.slideshow?.slides?.length ?? 0;
      const modalCount = this.refs.media?.length ?? 0;

      let slideshowIndex = mediaIndex;
      if (slideshowCount > modalCount) {
        const offset = slideshowCount - modalCount;
        slideshowIndex = Math.min(mediaIndex + offset, slideshowCount - 1);
      }

      mediaGallery?.slideshow?.select(slideshowIndex, undefined, { animate: true });
      return;
    }

    const { dialog, media } = this.refs;
    const targetImage = media[mediaIndex];

    const open = () => {
      if (!dialog.open) dialog.showModal();
    };

    /** @type {HTMLElement | null} */
    const sourceImage = event.target instanceof Element ? event.target.closest('li,slideshow-slide') : null;

    if (!supportsViewTransitions() || isLowPowerDevice() || !sourceImage || !targetImage) {
      open();
      this.#updateCounter(mediaIndex);
      this.selectThumbnail(mediaIndex, { behavior: 'instant' });
      return;
    }

    const transitionName = `gallery-item`;
    sourceImage.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      open();
      sourceImage.style.removeProperty('view-transition-name');
      targetImage.style.setProperty('view-transition-name', transitionName);
    });

    targetImage.style.removeProperty('view-transition-name');

    this.#updateCounter(mediaIndex);
    this.selectThumbnail(mediaIndex, { behavior: 'instant' });
  }

  /**
   * Loads a high-resolution image for a specific media container
   * @param {HTMLElement} mediaContainer - The media container element
   */
  #loadHighResolutionImage(mediaContainer) {
    if (!mediaContainer.classList.contains('product-media-container--image')) return false;

    const image = mediaContainer.querySelector('img.product-media__image');
    if (!image || !(image instanceof HTMLImageElement)) return false;

    const highResolutionUrl = image.getAttribute('data_max_resolution');
    if (!highResolutionUrl || this.#highResImagesLoaded.has(highResolutionUrl)) return false;

    preloadImage(highResolutionUrl);

    const newImage = new Image();
    newImage.className = image.className;
    newImage.alt = image.alt;
    newImage.setAttribute('data_max_resolution', highResolutionUrl);

    // When the high-resolution image loads, replace the existing image
    newImage.onload = () => {
      image.replaceWith(newImage);
      this.#highResImagesLoaded.add(highResolutionUrl);
    };

    newImage.src = highResolutionUrl;
  }

  /**
   * Handles the scroll event of the dialog, which is used to update the active thumbnail when the corresponding image is visible in the main view.
   * @param {Event} event - The scroll event.
   */
  handleScroll = debounce(async () => {
    const { media, thumbnails } = this.refs;

    const mostVisibleElement = await getMostVisibleElement(media);
    const activeIndex = media.indexOf(mostVisibleElement);
    const targetThumbnail = thumbnails.children[activeIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === activeIndex}`);
    });

    this.#loadHighResolutionImage(mostVisibleElement);
    this.#fitDialogToMedia(activeIndex);
    this.#updateCounter(activeIndex);
    this.dispatchEvent(new ZoomMediaSelectedEvent(activeIndex));
  }, 50);

  /**
   * Closes the zoom dialog.
   */
  async close() {
    const { dialog, media } = this.refs;
    const isProductPageDialog = !!this.closest("main[data-template*='product']");

    if (isProductPageDialog) {
      dialog.classList.add('dialog--closing-center');
      await onAnimationEnd(dialog);
      dialog.classList.remove('dialog--closing-center');
      this.closeDialog();
      return;
    }

    if (!supportsViewTransitions() || isLowPowerDevice()) return this.closeDialog();

    // Find the most visible image using IntersectionObserver
    const mostVisibleElement = await getMostVisibleElement(media);

    // Get the index and set up transition
    const activeIndex = media.indexOf(mostVisibleElement);
    const transitionName = `gallery-item`;

    const mediaGallery = /** @type {import('./media-gallery').MediaGallery | undefined} */ (
      this.closest('media-gallery')
    );

    const slideshowActive = mediaGallery?.presentation === 'carousel';

    const slide = slideshowActive ? mediaGallery.slideshow?.slides?.[activeIndex] : mediaGallery?.media?.[activeIndex];

    if (!slide) return this.closeDialog();

    dialog.classList.add('dialog--closed');

    await onAnimationEnd(this.refs.thumbnails);

    mostVisibleElement.style.setProperty('view-transition-name', transitionName);

    await startViewTransition(() => {
      mostVisibleElement.style.removeProperty('view-transition-name');
      slide.style.setProperty('view-transition-name', transitionName);
      this.closeDialog();
    });

    slide.style.removeProperty('view-transition-name');
    dialog.classList.remove('dialog--closed');
  }

  closeDialog() {
    const { dialog } = this.refs;
    dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.close();
  }

  /**
   * Handles the click event of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   */
  async handleThumbnailClick(index) {
    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.selectThumbnail(index, { behavior });
  }

  /**
   * Handles the pointer enter event of a thumbnail.
   * @param {number} index - The index of the thumbnail to load the high-resolution image for.
   */
  async handleThumbnailPointerEnter(index) {
    const normalizedIndex = Number(index);
    const { media } = this.refs;
    if (!Number.isFinite(normalizedIndex) || normalizedIndex < 0 || !media[normalizedIndex]) return;

    this.#loadHighResolutionImage(media[normalizedIndex]);
  }

  /**
   * Scrolls thumbnail strip horizontally.
   * @param {number|string} directionInput - -1 for left, 1 for right.
   */
  scrollThumbnails(directionInput) {
    const direction = Number(directionInput);
    const scroller = this.refs.thumbnails;
    if (!scroller || !(scroller instanceof HTMLElement) || !Number.isFinite(direction) || direction === 0) return;

    const firstThumb = scroller.querySelector('.dialog-thumbnails-list__thumbnail');
    const step = ((firstThumb instanceof HTMLElement ? firstThumb.clientWidth : 88) + 8) * 3;

    scroller.scrollBy({
      left: direction * step,
      behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    });
  }

  /**
   * Selects previous/next media from the modal gallery.
   * @param {number|string} stepInput - -1 previous, 1 next
   */
  selectAdjacent(stepInput) {
    const step = Number(stepInput);
    if (!Number.isFinite(step) || step === 0) return;

    const { thumbnails } = this.refs;
    if (!thumbnails || !(thumbnails instanceof HTMLElement)) return;

    const buttons = Array.from(thumbnails.querySelectorAll('button'));
    if (buttons.length === 0) return;

    let currentIndex = buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
    if (currentIndex < 0) currentIndex = 0;

    const max = buttons.length - 1;
    let nextIndex = currentIndex + Math.sign(step);
    if (nextIndex < 0) nextIndex = max;
    if (nextIndex > max) nextIndex = 0;

    const behavior = prefersReducedMotion() ? 'instant' : 'smooth';
    this.#updateCounter(nextIndex);
    this.selectThumbnail(nextIndex, { behavior });
  }

  /**
   * Handles the selection of a thumbnail.
   * @param {number} index - The index of the thumbnail to select.
   * @param {Object} options - The options for the selection.
   * @param {ScrollBehavior} options.behavior - The behavior of the scroll.
   */
  async selectThumbnail(index, options = { behavior: 'smooth' }) {
    const normalizedIndex = Number(index);
    if (!this.refs.thumbnails || !this.refs.thumbnails.children.length) return;

    // Guard if invalid
    if (!Number.isFinite(normalizedIndex) || normalizedIndex < 0 || normalizedIndex >= this.refs.thumbnails.children.length)
      return;

    const { media, thumbnails } = this.refs;
    const targetThumbnail = thumbnails.children[normalizedIndex];

    if (!targetThumbnail || !(targetThumbnail instanceof HTMLElement)) return;

    Array.from(thumbnails.querySelectorAll('button')).forEach((button, i) => {
      button.setAttribute('aria-selected', `${i === normalizedIndex}`);
    });

    this.#updateCounter(normalizedIndex);

    scrollIntoView(targetThumbnail, {
      ancestor: thumbnails,
      behavior: options.behavior,
      block: 'center',
      inline: 'center',
    });

    const targetImage = media[normalizedIndex];

    if (targetImage) {
      targetImage.scrollIntoView({
        behavior: options.behavior,
      });

      this.#loadHighResolutionImage(targetImage);
    }
    this.#fitDialogToMedia(normalizedIndex);
    this.dispatchEvent(new ZoomMediaSelectedEvent(normalizedIndex));
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}

/**
 * Get the most visible element from a list of elements.
 * @param {HTMLElement[]} elements - The elements to get the most visible element from.
 * @returns {Promise<HTMLElement>} A promise that resolves to the most visible element.
 */
function getMostVisibleElement(elements) {
  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      (entries) => {
        const mostVisible = entries.reduce((prev, current) =>
          current.intersectionRatio > prev.intersectionRatio ? current : prev
        );
        observer.disconnect();
        resolve(/** @type {HTMLElement} */ (mostVisible.target));
      },
      {
        threshold: Array.from({ length: 100 }, (_, i) => i / 100),
      }
    );

    for (const element of elements) {
      observer.observe(element);
    }
  });
}
